import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import {
  buildBannerViewedProps,
  resolveSurface,
} from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import {
  ALERT_CENTER_EVENTS,
  ALERT_CENTER_SOURCE,
  PAYWALL_VIEWED_ALERT_IDS,
  type tAlertAcknowledgedProps,
  type tAlertCenterPaywallViewedProps,
  type tAlertCenterSellerPausedViewedProps,
  type tAlertCtaSurface,
  type tAlertRowCtaClickedProps,
  type tAlertShownProps,
  type tPaywallViewedAlertId,
} from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { LEAD_AGENT_PAYWALL_EVENTS } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { resolveSuggestedTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.functions';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';

/**
 * RET-1891 — the center's replacement emitters for the two paywall "viewed"
 * events the gated legacy banners took with them (RET-1879).
 *
 * "Viewed" means EXPOSED, never eligible (product, 2026-08-21). Exposure has
 * two shapes in the center and this file owns both, because they share one
 * dedupe ledger:
 *
 * - `slot` alerts live in the bell panel and nowhere else, so exposure starts
 *   when the user opens the panel with the alert in the rendered list.
 * - `contextual` alerts have their own mounted anchors (sidebar avatar, funnel
 *   header), which are on screen without any user action, so exposure starts
 *   when an anchor mounts — the same moment the legacy strip emitted.
 *
 * Both are deduped ONCE PER PAGE LOAD per alert. The ledger is module-level
 * rather than a component ref for two reasons the ref cannot cover: the panel
 * is conditionally MOUNTED (KTD14), so a ref inside it dies on close and
 * reopening would re-emit; and the contextual alert has two independent
 * anchors that would each emit for the same strip on the same page.
 *
 * Keyed on `alert.key` (`<alert>:<workspace>[:<period>]`) rather than the bare
 * id, so a workspace switch inside one page load re-emits for the new
 * workspace — the bug the legacy banners had to fix with a per-workspace latch
 * reset.
 */
const exposedAlertKeys = new Set<string>();

/**
 * Clears the page-load ledger. Production never calls this: a real page load
 * gets a fresh module. Tests do, because vitest keeps the module between
 * cases in a file.
 */
export function resetAlertExposureLedger(): void {
  exposedAlertKeys.clear();
}

/** True the first time this alert key is seen in this page load. */
function claimExposure(alertKey: string): boolean {
  if (exposedAlertKeys.has(alertKey)) return false;
  exposedAlertKeys.add(alertKey);
  return true;
}

function isPaywallViewedAlertId(id: string): id is tPaywallViewedAlertId {
  return (PAYWALL_VIEWED_ALERT_IDS as readonly string[]).includes(id);
}

/**
 * Emits `la_paywall_banner_viewed` for every paywall alert the caller is
 * actually rendering. Called by the bell panel with its listed alerts.
 *
 * The payload is the legacy one verbatim (`buildBannerViewedProps`, the same
 * builder both retired banners used) plus `source` and `alert_id`. The
 * stamped values are read through a ref: a plan or countdown value changing
 * under an already-emitted alert is not a new exposure, so it must not be a
 * trigger. The effect keys on the exposed alerts themselves.
 */
export function useAlertPaywallExposureAnalytics(
  exposedAlerts: tLiveAlert[],
): void {
  const { trackEvent } = useAnalytics();
  // The REAL flag, not `areSurfacesEnabled`: with the dev simulator override
  // active on a flag-off workspace the panel must still render (that is the
  // local QA path), but the legacy banners are also still live there, so an
  // event from here would be both a phantom and a double count.
  const { isAlertCenterEnabled } = useFeatureFlagsContext();
  const location = useLocation();
  const { subscription } = useSubscriptionContext();
  const { acceptance, daysUntilPayment, bannerVariant } =
    useLeadAgentAcceptanceContext();

  const paywallAlerts = exposedAlerts.filter((alert) =>
    isPaywallViewedAlertId(alert.id),
  );
  // The exposure identity of the current list: which alerts, under which keys.
  const exposureSignature = paywallAlerts.map((alert) => alert.key).join('|');

  const stampRef = useRef({
    paywallAlerts,
    pathname: location.pathname,
    subscription,
    acceptance,
    daysUntilPayment,
    bannerVariant,
  });
  stampRef.current = {
    paywallAlerts,
    pathname: location.pathname,
    subscription,
    acceptance,
    daysUntilPayment,
    bannerVariant,
  };

  useEffect(() => {
    if (!isAlertCenterEnabled || !exposureSignature) return;
    const stamp = stampRef.current;
    // Legacy parity: `surface` is resolved from the route exactly as the
    // banners did. Both declarations are scoped to LEAD_AGENT_SURFACES, so a
    // listed alert always sits on one of them; a null here means the route
    // moved out from under the panel and there is no faithful payload to send.
    const surface = resolveSurface(stamp.pathname);
    if (!surface) return;

    stamp.paywallAlerts.forEach((alert) => {
      if (!isPaywallViewedAlertId(alert.id)) return;
      if (!claimExposure(alert.key)) return;

      const props: tAlertCenterPaywallViewedProps = {
        ...buildBannerViewedProps({
          variant: stamp.bannerVariant,
          daysUntilPayment: stamp.daysUntilPayment,
          planCode: stamp.subscription?.planCode,
          frequency: stamp.subscription?.frequency,
          suggestedTier: resolveSuggestedTier(stamp.acceptance?.suggestedTier),
          surface,
          workspaceBucket: stamp.acceptance?.workspaceBucket ?? null,
        }),
        alert_id: alert.id,
        source: ALERT_CENTER_SOURCE,
      };
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.BANNER_VIEWED, props);
    });
  }, [exposureSignature, isAlertCenterEnabled, trackEvent]);
}

/** The one `contextual` declaration that carries a legacy "viewed" event. */
export const SELLER_PAUSED_ALERT_ID = 'la-seller-paused';

/**
 * Emits `la_seller_bot_paused_viewed` when a contextual anchor mounts with the
 * seller-paused alert live. Called by every anchor that renders it; the shared
 * ledger collapses them to one event per page load, so the sidebar avatar and
 * the funnel header chip do not report the same strip twice.
 *
 * `pause_reason` mirrors the legacy strip, including its `'unknown'` fallback
 * for a pause the backend could not attribute.
 */
export function useSellerPausedExposureAnalytics(
  contextualAlert: tLiveAlert | null,
): void {
  const { trackEvent } = useAnalytics();
  const { isAlertCenterEnabled } = useFeatureFlagsContext();
  const { sellerPauseReason } = useAlertCenterContext();

  const alertKey =
    contextualAlert?.id === SELLER_PAUSED_ALERT_ID ? contextualAlert.key : null;

  const pauseReasonRef = useRef(sellerPauseReason);
  pauseReasonRef.current = sellerPauseReason;

  useEffect(() => {
    if (!isAlertCenterEnabled || !alertKey) return;
    if (!claimExposure(alertKey)) return;

    const props: tAlertCenterSellerPausedViewedProps = {
      pause_reason: pauseReasonRef.current || 'unknown',
      source: ALERT_CENTER_SOURCE,
    };
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.SELLER_PAUSED_VIEWED, props);
  }, [alertKey, isAlertCenterEnabled, trackEvent]);
}

/**
 * RET-1957 (U4) — `front.alerts.row.cta_clicked`. The constant shipped with
 * RET-1875 and nothing ever emitted it, which left the RET-1956 payment path
 * (a `detail` CTA on `la-expired` / `la-acceptance`) with a "viewed" number
 * and no "acted" number: the funnel had a top and no bottom.
 *
 * Called by the SHARED dispatch (`useAlertCta`), the one place every surface's
 * CTA passes through — a per-surface emitter is how "the click counts in the
 * bell but not in the tab" happens, and this family already has four surfaces.
 *
 * The surface is DECLARED by the caller rather than inferred from the alert:
 * the bell panel lists waiting P0s, whose placement is `wide`, so inferring it
 * would drop real bell clicks. Declaring it also gives the wide strip its
 * silence for free — `AlertBanner` names no surface because it emits its own
 * `BANNER_CTA_CLICKED` around the same click, and one action landing in two
 * funnels corrupts both.
 *
 * Flag off emits nothing: no center surface is mounted, so the event would be
 * a phantom. Same gate the exposure emitters above use, and for the same
 * reason it is the REAL flag rather than `areSurfacesEnabled`.
 */
/**
 * Per-family additions to `row.cta_clicked`, plus the ability to correct a
 * base field. AGI-428 needs both: its notice stamps `segment` / `urgency` /
 * `key_account` / `cta_url`, and its button is an EXTERNAL link even though
 * the declaration's CTA kind is `detail` (that is what opened the dialog the
 * button lives in). Kept deliberately narrow: an emitter that accepts
 * arbitrary objects is how a payload grows a field nobody can explain.
 */
export type tAlertCtaClickOverrides = Partial<tAlertRowCtaClickedProps> &
  Record<string, string | number | boolean | undefined>;

export function useAlertCtaAnalytics(
  surface: tAlertCtaSurface | undefined,
): (alert: tLiveAlert, overrides?: tAlertCtaClickOverrides) => void {
  const { trackEvent } = useAnalytics();
  const { isAlertCenterEnabled } = useFeatureFlagsContext();

  return useCallback(
    (alert: tLiveAlert, overrides?: tAlertCtaClickOverrides) => {
      if (!surface || !isAlertCenterEnabled) return;
      const { cta } = alert.copy;
      // No CTA means the dispatch is about to no-op; a click that does nothing
      // is not an action taken.
      if (!cta) return;

      const props: tAlertRowCtaClickedProps = {
        alert_id: alert.id,
        priority: alert.priority,
        cta_kind: cta.kind,
        placement: alert.placement,
        source: surface,
        ...overrides,
      };
      trackEvent(ALERT_CENTER_EVENTS.ROW_CTA_CLICKED, props);
    },
    [surface, isAlertCenterEnabled, trackEvent],
  );
}

/**
 * U9 (R19) — `front.alerts.alert.shown` and `front.alerts.alert.acknowledged`.
 *
 * Both constants shipped with RET-1875 and nothing ever emitted the second
 * one, so every dismissible alert had a "seen" story built from CTA clicks and
 * no "put away" story at all. The first-red explainer needs both halves: its
 * whole success criterion is that people read it once and never again.
 *
 * The ledger prefix keeps this event's dedupe separate from the paywall
 * emitters', which key on the bare `alert.key` in the same module-level set —
 * an alert reaching both would otherwise silence one of them.
 */
const SHOWN_LEDGER_PREFIX = 'shown:';

/**
 * Reports an alert as exposed on a surface the user did not have to open.
 * Pass the alert the surface is actually rendering, or null.
 *
 * Once per alert key per page load, on the shared ledger: navigating away and
 * back remounts this surface, and a second event there would say the user met
 * the alert twice when they met it once.
 */
export function useAlertShownAnalytics(
  alert: tLiveAlert | null,
  surface: tAlertCtaSurface,
): void {
  const { trackEvent } = useAnalytics();
  // The REAL flag, for the same reason the emitters above use it: the dev
  // simulator can mount this surface on a flag-off workspace.
  const { isAlertCenterEnabled } = useFeatureFlagsContext();

  // Read through a ref so a copy value changing under an already-reported
  // alert is not a new exposure; the effect keys on identity, not content.
  const alertRef = useRef(alert);
  alertRef.current = alert;
  const alertKey = alert?.key ?? null;

  useEffect(() => {
    if (!isAlertCenterEnabled || !alertKey) return;
    const current = alertRef.current;
    if (!current) return;
    if (!claimExposure(`${SHOWN_LEDGER_PREFIX}${alertKey}`)) return;

    const props: tAlertShownProps = {
      alert_id: current.id,
      priority: current.priority,
      source: surface,
    };
    trackEvent(ALERT_CENTER_EVENTS.ALERT_SHOWN, props);
  }, [alertKey, surface, isAlertCenterEnabled, trackEvent]);
}

/**
 * Reports a dismissal, stamped with the surface it came from. Returned as a
 * callback rather than run in an effect: acknowledgment is an act, and the
 * only honest moment to count it is the click.
 *
 * Never gates the dismissal itself — the caller acknowledges first and reports
 * after, so a flag-off (or a dropped event) can never leave a user staring at
 * a banner they already put away.
 */
export function useAlertAcknowledgedAnalytics(
  surface: tAlertCtaSurface,
): (alert: tLiveAlert) => void {
  const { trackEvent } = useAnalytics();
  const { isAlertCenterEnabled } = useFeatureFlagsContext();

  return useCallback(
    (alert: tLiveAlert) => {
      if (!isAlertCenterEnabled) return;
      const props: tAlertAcknowledgedProps = {
        alert_id: alert.id,
        // The resolved occurrence, never re-derived: it is what the
        // acknowledged KEY carries, so a period-scoped dismissal can be
        // matched back to the episode it muted.
        period_key: alert.occurrence,
        source: surface,
      };
      trackEvent(ALERT_CENTER_EVENTS.ALERT_ACKNOWLEDGED, props);
    },
    [surface, isAlertCenterEnabled, trackEvent],
  );
}
